import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { LucideIcon } from 'lucide-react';
import {
  Calendar,
  CreditCard,
  Bell,
  CheckCircle,
  MessageCircle,
  CalendarPlus,
  Clock,
  User,
  FileText,
} from 'lucide-react';

export type HeroNotificationPoolItem = {
  id: string;
  Icon: LucideIcon;
  iconBg: string;
  iconColor: string;
  title: string;
  sub: string;
};

const NOTIFICATION_IDS = [
  'rdv',
  'forfait',
  'rappel',
  'message',
  'reservation',
  'paiement',
  'devis',
  'client',
  'seance',
  'rappel_prog',
] as const;

type NotificationId = (typeof NOTIFICATION_IDS)[number];

const NOTIFICATION_META: Record<
  NotificationId,
  Pick<HeroNotificationPoolItem, 'Icon' | 'iconBg' | 'iconColor'>
> = {
  rdv: { Icon: Calendar, iconBg: '#FFF4E5', iconColor: '#FFB84C' },
  forfait: { Icon: CreditCard, iconBg: '#E7F9ED', iconColor: '#27C05D' },
  rappel: { Icon: Bell, iconBg: '#E0F2FE', iconColor: '#0EA5E9' },
  message: { Icon: MessageCircle, iconBg: '#F3E8FF', iconColor: '#8B5CF6' },
  reservation: { Icon: CalendarPlus, iconBg: '#FEF3C7', iconColor: '#D97706' },
  paiement: { Icon: CreditCard, iconBg: '#ECFDF5', iconColor: '#059669' },
  devis: { Icon: FileText, iconBg: '#EFF6FF', iconColor: '#2563EB' },
  client: { Icon: User, iconBg: '#FDF4FF', iconColor: '#C026D3' },
  seance: { Icon: CheckCircle, iconBg: '#F0FDF4', iconColor: '#16A34A' },
  rappel_prog: { Icon: Clock, iconBg: '#FEF9C3', iconColor: '#CA8A04' },
};

/** Données pour les notifications type « feed » (section mobile LP V2). */
export function useHeroNotificationsPool(): HeroNotificationPoolItem[] {
  const { t } = useTranslation('marketing');

  return useMemo(
    () =>
      NOTIFICATION_IDS.map((id) => ({
        id,
        ...NOTIFICATION_META[id],
        title: t(`heroNotificationsPool.items.${id}.title`),
        sub: t(`heroNotificationsPool.items.${id}.sub`),
      })),
    [t]
  );
}
